import { Accordion, AccordionItems } from '@Components/common/accordion'
import { PlayslipMessageCatalogEntry } from '@Generated/back-office-client/src'
import { Flex, Heading, Skeleton, Text, TextArea, TextField } from '@radix-ui/themes'
import { Accordion as RadixAccordion } from 'radix-ui'
import { useState } from 'react'
import { LONG_MESSAGE_LENGTH, MESSAGE_GROUPS, MESSAGES_LOAD_FAILED_MESSAGE } from './constants'
import { ThemeMessageErrors, ThemeMessagesFormData } from './types'

type ThemeMessagesProps = {
  catalog: PlayslipMessageCatalogEntry[] | undefined
  isLoading: boolean
  loadFailed: boolean
  messages: ThemeMessagesFormData
  errors: ThemeMessageErrors
  onChange: (key: string, text: string) => void
  onBlur: (entry: PlayslipMessageCatalogEntry) => void
}

/**
 * The playslip copy a theme rewords, one accordion item per screen. While the filter has text, only
 * matching messages show and every group holding one is open.
 */
export const ThemeMessages = ({
  catalog,
  isLoading,
  loadFailed,
  messages,
  errors,
  onChange,
  onBlur,
}: ThemeMessagesProps) => {
  const [filter, setFilter] = useState('')
  const [openGroups, setOpenGroups] = useState<string[]>([])
  const query = filter.trim().toLowerCase()

  const matches = (entry: PlayslipMessageCatalogEntry) =>
    !query ||
    [entry.key, entry.defaultText, messages[entry.key] ?? ''].some((text) =>
      text.toLowerCase().includes(query),
    )

  const groups = MESSAGE_GROUPS.map(({ group, label }) => {
    const inGroup = (catalog ?? []).filter((entry) => entry.group === group)
    const reworded = inGroup.filter((entry) => messages[entry.key]?.trim()).length

    return {
      group,
      name: reworded > 0 ? `${label} (${reworded} reworded)` : label,
      entries: inGroup.filter(matches),
    }
  }).filter(({ entries }) => entries.length > 0)

  const items: AccordionItems[] = groups.map(({ group, name, entries }) => ({
    id: group,
    value: group,
    name,
    child: (
      <RadixAccordion.Content className="AccordionContent">
        <Flex direction="column" gap="3" p="3">
          {entries.map((entry) => (
            <MessageField
              key={entry.key}
              entry={entry}
              value={messages[entry.key] ?? ''}
              error={errors[entry.key]}
              onChange={onChange}
              onBlur={onBlur}
            />
          ))}
        </Flex>
      </RadixAccordion.Content>
    ),
  }))

  return (
    <Flex direction="column" gap="2">
      <Heading size="4">Copy</Heading>
      <Text size="2" color="gray">
        A field left blank shows the app&apos;s built-in text. Words in braces, like {'{count}'},
        are filled in by the app.
      </Text>

      {isLoading && (
        <Skeleton loading={true}>
          <div className="playslip-theme-skeleton-card" />
        </Skeleton>
      )}

      {loadFailed && (
        <Text size="2" color="red">
          {MESSAGES_LOAD_FAILED_MESSAGE}
        </Text>
      )}

      {catalog && (
        <>
          <label htmlFor="playslip-theme-messages-filter">
            <Text size="2" weight="medium">
              Filter copy
            </Text>
          </label>
          <TextField.Root
            id="playslip-theme-messages-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <Accordion
            items={items}
            values={query ? groups.map(({ group }) => group) : openGroups}
            onValueChange={query ? undefined : setOpenGroups}
          />
        </>
      )}
    </Flex>
  )
}

type MessageFieldProps = {
  entry: PlayslipMessageCatalogEntry
  value: string
  error: string | undefined
  onChange: (key: string, text: string) => void
  onBlur: (entry: PlayslipMessageCatalogEntry) => void
}

/** One message, labelled by its default, which also shows as the placeholder while it is blank. */
const MessageField = ({ entry, value, error, onChange, onBlur }: MessageFieldProps) => {
  const id = `playslip-theme-message-${entry.key}`
  const inputProps = {
    id,
    value,
    placeholder: entry.defaultText,
    onBlur: () => onBlur(entry),
  }

  return (
    <Flex direction="column" gap="1">
      <label htmlFor={id}>
        <Text size="2" weight="medium">
          {entry.defaultText}
        </Text>
      </label>
      {entry.defaultText.length > LONG_MESSAGE_LENGTH ? (
        <TextArea {...inputProps} onChange={(e) => onChange(entry.key, e.target.value)} />
      ) : (
        <TextField.Root {...inputProps} onChange={(e) => onChange(entry.key, e.target.value)} />
      )}
      {entry.description && (
        <Text size="1" color="gray">
          {entry.description}
        </Text>
      )}
      {error && (
        <Text size="1" color="red">
          {error}
        </Text>
      )}
    </Flex>
  )
}
